import { useState, useEffect, useRef } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'

// Search-and-pick control for manually linking an analytic to a policy rule.
// Filters out already-linked analytics, supports free-text search and a
// multi-select section filter, and calls onSelect(analyticId) on a pick.
//
// policySectionIds scopes the linkable analytics to the section(s) selected at
// the policy level, so (e.g.) a PCard policy can't link Accounts Payable
// analytics. When the policy has no sections selected we fall back to all.
const AnalyticSearch = ({ allAnalytics, linkedAnalyticIds, onSelect, policySectionIds = [] }) => {
  const [search, setSearch] = useState('')
  const [selectedSections, setSelectedSections] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [showSectionPicker, setShowSectionPicker] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShowDropdown(false)
        setShowSectionPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const scopedAnalytics = policySectionIds.length > 0
    ? allAnalytics.filter((a) => policySectionIds.includes(a.section_id))
    : allAnalytics

  const sections = [...new Set(scopedAnalytics.map((a) => a.section_name).filter(Boolean))].sort()
  // The per-search section filter only helps when more than one section is in
  // scope; with a single-section policy it's redundant noise.
  const showSectionFilter = sections.length > 1

  const toggleSection = (s) => {
    setSelectedSections((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    )
  }

  const available = scopedAnalytics.filter((a) =>
    !linkedAnalyticIds.has(a.id) &&
    (selectedSections.length === 0 || selectedSections.includes(a.section_name)) &&
    (a.name?.toLowerCase().includes(search.toLowerCase()) ||
     a.key?.toLowerCase().includes(search.toLowerCase()))
  )

  const sectionLabel = selectedSections.length === 0
    ? 'All sections'
    : selectedSections.length === 1
      ? selectedSections[0]
      : `${selectedSections.length} sections`

  return (
    <div ref={ref} className="position-relative mt-2">
      <div className="d-flex gap-2">
        <Form.Control
          type="text"
          size="sm"
          placeholder="Search analytics to link..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setShowDropdown(true) }}
          onFocus={() => { setShowDropdown(true); setShowSectionPicker(false) }}
        />
        {showSectionFilter && (
          <div className="position-relative" style={{ minWidth: '150px' }}>
            <Button
              variant="light-primary"
              size="sm"
              className={`w-100 text-start text-truncate${selectedSections.length > 0 ? ' border-primary text-primary' : ''}`}
              onClick={() => { setShowSectionPicker((v) => !v); setShowDropdown(false) }}
            >
              {sectionLabel}
            </Button>
            {showSectionPicker && (
              <div
                className="position-absolute bg-white border rounded shadow-sm"
                style={{ zIndex: 20, minWidth: '150px', maxHeight: '200px', overflowY: 'auto', right: 0, top: '100%' }}
              >
                {sections.map((s) => (
                  <label
                    key={s}
                    className="d-flex align-items-center px-2 py-1 small gap-2"
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap', margin: 0 }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(s)}
                      onChange={() => toggleSection(s)}
                      style={{ cursor: 'pointer' }}
                    />
                    {s}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showDropdown && (
        <div
          className="position-absolute bg-white border rounded shadow-sm w-100"
          style={{ zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}
        >
          {available.length === 0 ? (
            <div className="p-2 text-muted small">No matching analytics found</div>
          ) : (
            available.map((a) => (
              <div
                key={a.id}
                className="px-2 py-1 small border-bottom"
                style={{ cursor: 'pointer' }}
                onMouseDown={() => {
                  onSelect(a.id)
                  setSearch('')
                  setShowDropdown(false)
                }}
              >
                <div>
                  <span className="fw-bold">{a.name}</span>
                  <span className="text-muted ms-2">{a.key}</span>
                </div>
                {a.description && (
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>{a.description}</div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default AnalyticSearch
